import type { PublicBranding } from '@schemas/rebilling';
import type { TenantBranding } from '@schemas/tenant';
import type { CSSProperties } from 'react';

/**
 * White-label theme. Every brand color in the UI comes from these values through CSS
 * custom properties, so an agency's branding re-skins the whole app.
 *
 * Same approach as ClientForce's brand theming (lib/brand-theming.ts: a brand's
 * primary/secondary color and font become CSS variables), fed by our real
 * `agency.branding` instead of browser storage. The design tokens in
 * src/styles/global.css read --primary / --accent-brand (RGB channels, for Tailwind's
 * opacity modifiers); --color-primary / --color-accent keep the hex values.
 */
export interface Theme {
  brandName: string;
  logoUrl?: string;
  /** Browser tab icon (white-label) */
  faviconUrl?: string;
  /** Platform name for the "Powered by" line; absent: the line is not shown */
  poweredBy?: string;
  colors: {
    primary: string;
    primaryContrast: string;
    accent: string;
  };
  /** Brand font, one of BRAND_FONTS (absent: the design system's Inter) */
  font?: string;
}

/** Fonts a brand may pick (ClientForce's list, limited to stacks that need no download) */
export const BRAND_FONTS: Readonly<Record<string, string>> = {
  'System Default': 'system-ui, -apple-system, sans-serif',
  Inter: '"Inter Variable", Inter, sans-serif',
  Georgia: 'Georgia, serif',
  'Times New Roman': '"Times New Roman", serif',
};

export const defaultTheme: Theme = {
  brandName: 'Agency Console',
  colors: {
    primary: '#2563eb',
    primaryContrast: '#ffffff',
    accent: '#7c3aed',
  },
};

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function hexColor(value: unknown): string | undefined {
  return typeof value === 'string' && HEX_COLOR.test(value) ? value : undefined;
}

function httpsUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.toString() : undefined;
  } catch {
    // Not a URL: ignore the logo rather than render a broken image.
    return undefined;
  }
}

const DARK_TEXT = '#111827';

/** WCAG 2.x relative luminance of an sRGB colour */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = rgbChannels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colours (1 to 21) */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Black or white text, whichever has the higher WCAG contrast on the given background. */
export function contrastColor(hex: string): string {
  return contrastRatio(hex, DARK_TEXT) > contrastRatio(hex, '#ffffff') ? DARK_TEXT : '#ffffff';
}

/** "#2563eb" -> [37, 99, 235] */
export function rgbChannels(hex: string): [number, number, number] {
  const full = hex.length === 4 ? `#${[...hex.slice(1)].map((c) => c + c).join('')}` : hex;
  return [1, 3, 5].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
}

const channels = (hex: string) => rgbChannels(hex).join(' ');

/**
 * Builds a theme from an agency's `branding` JSON (TenantBranding-shaped keys).
 * Unknown or invalid values fall back to the default theme.
 */
export function themeFromBranding(brandName: string, branding: Record<string, unknown>): Theme {
  const keys: Partial<Record<keyof TenantBranding | 'appName', unknown>> = branding;
  const primary = hexColor(keys.primaryColor) ?? defaultTheme.colors.primary;
  const logoUrl = httpsUrl(keys.logoUrl);
  const faviconUrl = httpsUrl(keys.faviconUrl);
  const appName = typeof keys.appName === 'string' && keys.appName.trim() ? keys.appName.trim() : null;
  const fontKey = branding.fontFamily;
  const font = typeof fontKey === 'string' ? BRAND_FONTS[fontKey] : undefined;
  return {
    ...(font ? { font } : {}),
    brandName: appName ?? brandName,
    ...(logoUrl ? { logoUrl } : {}),
    ...(faviconUrl ? { faviconUrl } : {}),
    colors: {
      primary,
      primaryContrast: contrastColor(primary),
      accent: hexColor(keys.secondaryColor) ?? defaultTheme.colors.accent,
    },
  };
}

/** The CSS variables a theme sets (on .theme-root and on <html>, so portalled menus follow the brand too) */
export function themeVariables(theme: Theme): Record<string, string> {
  return {
    '--color-primary': theme.colors.primary,
    '--color-primary-contrast': theme.colors.primaryContrast,
    '--color-accent': theme.colors.accent,
    '--primary': channels(theme.colors.primary),
    '--primary-foreground': channels(theme.colors.primaryContrast),
    '--accent-brand': channels(theme.colors.accent),
    ...(theme.font ? { '--brand-font': theme.font } : {}),
  };
}

export function themeStyle(theme: Theme): CSSProperties {
  return themeVariables(theme) as CSSProperties;
}

/** The theme for the public branding (GET /branding): the agency's look, and "Powered by" when it is on */
export function themeFromPublicBranding(branding: PublicBranding): Theme {
  const theme = themeFromBranding(branding.appName, {
    logoUrl: branding.logoUrl,
    faviconUrl: branding.faviconUrl,
    primaryColor: branding.primaryColor,
    secondaryColor: branding.secondaryColor,
  });
  return branding.showPoweredBy ? { ...theme, poweredBy: branding.poweredBy } : theme;
}
